// Catalog.ID Client — Claims List Pane
// ============================================================================

import { createListPaneType, renderPaneActions } from '@catalog-shell';
import type { ListPaneConfig, PaneTypeRenderContext, PaneActionDescriptor } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import type { ValidationStatus } from '../components/certificate-badge';
import {
    derivePublicationStatus,
    PUBLICATION_STATUS_COLORS,
    PUBLICATION_STATUS_LABELS,
    type PublicationStatus,
} from '../components/publication-status';
import * as claimsApi from '../api/claims';
import * as publicClaimsApi from '../api/public-claims';
import type { ClaimSummary, PublicClaimSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

/** A claim joined with its publicclaim row (if it has ever been published). */
interface ClaimRow extends ClaimSummary {
    publication: PublicClaimSummary | null;
}


const CLAIM_TYPE_LABELS: Record<string, string> = {
    email: 'Email', phone: 'Phone', postal_address: 'Address',
    bank_account: 'Bank Account', date_of_birth: 'Date of Birth',
    display_name: 'Display Name', professional_role: 'Role',
    organization: 'Organization', organization_name: 'Org Name',
    description: 'Description', website: 'Website', location: 'Location',
    biography: 'Biography', domain: 'Domain', registry_signing_key: 'Registry Key',
    label: 'Label', agent_type: 'Agent Type',
    // Display name only. `chat_address` is created programmatically by the
    // contact flow, so — like `registry_account_stream` — it is deliberately
    // absent from CLAIM_TYPES_BY_IDENTITY, PUBLISHABLE_BY and LABEL_TYPES in
    // claim-detail-pane.ts: never offered in the generic claim form, never
    // publishable (chat migration plan §4/§14).
    chat_address: 'Chat Address',
};

export function claimTypeLabel(type: string): string {
    return CLAIM_TYPE_LABELS[type] ?? type;
}

const STATUS_COLORS: Record<ValidationStatus, string> = {
    unvalidated: '#555',
    pending: 'rgba(100,149,237,0.3)',
    validated: 'rgba(80,200,80,0.3)',
    expired: 'rgba(220,150,50,0.3)',
    revoked: 'rgba(220,50,50,0.3)',
};

const STATUS_LABELS: Record<ValidationStatus, string> = {
    unvalidated: 'Unvalidated',
    pending: 'Pending',
    validated: 'Validated',
    expired: 'Expired',
    revoked: 'Revoked',
};

const columns: ColumnDef<ClaimRow>[] = [
    {
        key: 'claimType',
        header: 'Type',
        width: 120,
        sortable: true,
        getValue: (item) => item.claimType,
        type: {
            kind: 'text',
            converter: (v) => claimTypeLabel(v as string),
        },
    },
    {
        key: 'encryptedLabel',
        header: 'Label',
        width: 180,
        getValue: (item) => item.encryptedLabel,
        type: {
            kind: 'text',
            converter: (v) => v ? '(encrypted)' : '—',
            textColor: () => '#555',
        },
    },
    {
        key: 'validationStatus',
        header: 'Status',
        width: 110,
        getValue: (item) => {
            // The list doesn't have certificate data, so use server status directly.
            // deriveValidationStatus with empty certificates would misreport "validated" as "expired".
            if (item.revokedAt) return 'revoked';
            return item.validationStatus ?? 'unvalidated';
        },
        type: {
            kind: 'badge',
            backgroundColor: (v) => STATUS_COLORS[(v as ValidationStatus)] ?? '#555',
            converter: (v) => STATUS_LABELS[(v as ValidationStatus)] ?? String(v),
        },
    },
    {
        key: 'publication',
        header: 'Publication',
        width: 120,
        // Validation (the badge above) and publication are different
        // lifecycles: a validated claim may still be waiting for operator
        // review before it is publicly resolvable.
        getValue: (item) => derivePublicationStatus(item.publication),
        type: {
            kind: 'badge',
            backgroundColor: (v) => PUBLICATION_STATUS_COLORS[v as PublicationStatus] || '',
            converter: (v) => v === 'none' ? '—' : PUBLICATION_STATUS_LABELS[v as PublicationStatus],
        },
    },
    {
        key: 'creationTime',
        header: 'Created',
        width: 100,
        sortable: true,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
        responsiveHide: { minContainerWidth: 600, priority: 1 },
    },
];

function createClaimsDataSource(): DataSource<ClaimRow> {
    return new DataSource<ClaimRow>({
        fetch: async (params): Promise<FetchResult<ClaimRow>> => {
            // Publication state is supplementary — if the lookup fails, show
            // the claims without it rather than failing the whole list.
            const [claims, publications] = await Promise.all([
                claimsApi.listClaims(),
                publicClaimsApi.listPublicClaims().catch((): PublicClaimSummary[] => []),
            ]);
            const pubByClaim = new Map(publications.map(p => [p.claimUuid, p]));
            const rows: ClaimRow[] = claims.map(c => ({
                ...c,
                publication: pubByClaim.get(c.claimUuid) ?? null,
            }));
            const sorted = [...rows];
            if (params.sortBy) {
                const key = params.sortBy;
                const dir = params.sortDirection === 'desc' ? -1 : 1;
                sorted.sort((a, b) => {
                    const va = (a as unknown as Record<string, unknown>)[key] ?? '';
                    const vb = (b as unknown as Record<string, unknown>)[key] ?? '';
                    return va < vb ? -dir : va > vb ? dir : 0;
                });
            }
            const sliced = sorted.slice(params.offset, params.offset + params.limit);
            return { items: sliced, totalCount: rows.length };
        },
        keyFn: (item) => item.claimUuid,
        pageSize: 100,
    });
}

/**
 * The claims list's title-row verbs. "Shared With Me" and "Public Claims" open
 * SIBLING panes rather than switching a scope on this one: the three scopes
 * carry different item shapes and open three different detail panes, so a
 * filter-bar scope switch would have to swap the columns and the row handler
 * with them.
 *
 * Registry Keys: per-machine signing key management lives in the standalone
 * registry module (Phase 5). Cld-id no longer hosts that UI; the registry
 * module's own dashboard pane is the source of truth.
 */
export function claimsListActions(ctx: PaneTypeRenderContext): PaneActionDescriptor[] {
    const open = (typeId: string, title: string, context?: Record<string, unknown>) => () => {
        ctx.surface?.openPane(
            { typeId, title, context },
            { originPaneId: ctx.instance.instanceId },
        );
    };
    return [
        {
            key: 'new-claim',
            label: '+ New Claim',
            variant: 'primary',
            execute: open(PANE_IDS.claimDetail, 'New Claim', { mode: 'new' }),
        },
        { key: 'shared-with-me', label: 'Shared With Me', execute: open(PANE_IDS.sharedWithMe, 'Shared With Me') },
        { key: 'public-claims', label: 'Public Claims', execute: open(PANE_IDS.publicClaims, 'Public Claims') },
    ];
}

const config: ListPaneConfig<ClaimRow> = {
    typeId: PANE_IDS.claimsList,
    defaultTitle: 'Claims',
    createDataSource: () => createClaimsDataSource(),
    columns,
    keyFn: (item) => item.claimUuid,
    initialMode: 'table',
    enabledModes: ['table'],
    selectionMode: 'none',
    onCreated: ({ dataSource }, ctx) => {
        ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
            const type = (data as { type?: string })?.type;
            // 'publicclaims' too: the Publication column reflects publish /
            // review / unpublish events that don't touch the claim rows.
            if (type === 'claims' || type === 'publicclaims') {
                dataSource.refresh();
            }
        }));
    },
    onItemSelect: (item, ctx) => {
        ctx.surface?.openPane({
            typeId: PANE_IDS.claimDetail,
            title: claimTypeLabel(item.claimType),
            context: { claimUuid: item.claimUuid },
        }, { originPaneId: ctx.instance.instanceId });
    },
    // The list's own verbs belong in its title row, not in a body toolbar
    // (shell USAGE.md "Where a button belongs").
    tabBarActions: (ctx) => renderPaneActions(claimsListActions(ctx)),
    ariaLabel: 'Claims list',
};

export function createClaimsListPane() {
    return createListPaneType(config);
}
